import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Pencil, X, Check } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCommissions() {
  const { token } = useAuth();
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [defaultSettings, setDefaultSettings] = useState({ default_rate: 10, service_fee: 0, min_commission: 0 });
  const [editingDefault, setEditingDefault] = useState(false);
  const [tempDefault, setTempDefault] = useState(10);
  const [formData, setFormData] = useState({
    name: "",
    rule_type: "global",
    category_id: "",
    vendor_id: "",
    commission_rate: 10,
    service_fee: 0,
    min_commission: 0,
    is_active: true
  });

  useEffect(() => {
    fetchRules();
    fetchCategories();
    fetchDefaultSettings();
  }, []);

  const fetchDefaultSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/commission-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDefaultSettings(data);
        setTempDefault(data.default_rate || 10);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const saveDefaultSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/commission-settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ default_rate: tempDefault })
      });
      if (res.ok) {
        setDefaultSettings({ ...defaultSettings, default_rate: tempDefault });
        setEditingDefault(false);
        alert("Varsayılan komisyon oranı güncellendi!");
      }
    } catch (err) {
      console.error(err);
      alert("Hata oluştu!");
    }
  };

  const fetchRules = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/commissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRules(data.rules || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/categories`);
      const data = await res.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingRule 
        ? `${API_URL}/api/admin/commissions/${editingRule.id}`
        : `${API_URL}/api/admin/commissions`;
      
      const res = await fetch(url, {
        method: editingRule ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        fetchRules();
        setShowForm(false);
        setEditingRule(null);
        setFormData({
          name: "",
          rule_type: "global",
          category_id: "",
          vendor_id: "",
          commission_rate: 10,
          service_fee: 0,
          min_commission: 0,
          is_active: true
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm("Bu kuralı silmek istediğinize emin misiniz?")) return;
    
    try {
      await fetch(`${API_URL}/api/admin/commissions/${ruleId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRules();
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      rule_type: rule.rule_type,
      category_id: rule.category_id || "",
      vendor_id: rule.vendor_id || "",
      commission_rate: rule.commission_rate,
      service_fee: rule.service_fee,
      min_commission: rule.min_commission,
      is_active: rule.is_active
    });
    setShowForm(true);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Komisyon Yönetimi</h1>
        <button
          onClick={() => { setShowForm(true); setEditingRule(null); }}
          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
        >
          + Yeni Kural
        </button>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg relative">
          <h3 className="font-medium text-blue-800">Varsayılan Komisyon</h3>
          
          {editingDefault ? (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-2xl font-bold text-blue-900">%</span>
              <input
                type="number"
                value={tempDefault}
                onChange={(e) => setTempDefault(parseFloat(e.target.value))}
                className="w-20 text-2xl font-bold text-blue-900 border-2 border-blue-400 rounded px-2 py-1"
                min="0"
                max="100"
                step="0.1"
              />
              <button
                onClick={saveDefaultSettings}
                className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600"
                title="Kaydet"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={() => { setEditingDefault(false); setTempDefault(defaultSettings.default_rate); }}
                className="p-2 bg-gray-400 text-white rounded-full hover:bg-gray-500"
                title="İptal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-blue-900">%{defaultSettings.default_rate}</p>
              <button
                onClick={() => setEditingDefault(true)}
                className="p-2 bg-blue-200 text-blue-700 rounded-full hover:bg-blue-300"
                title="Düzenle"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>
          )}
          
          <p className="text-sm text-blue-600 mt-1">Tüm kategorilerde geçerli</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="font-medium text-green-800">Aktif Kurallar</h3>
          <p className="text-2xl font-bold text-green-900">{rules.filter(r => r.is_active).length}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <h3 className="font-medium text-purple-800">Kategori Kuralları</h3>
          <p className="text-2xl font-bold text-purple-900">{rules.filter(r => r.rule_type === "category").length}</p>
        </div>
      </div>

      {/* Rules Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kural Adı</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tür</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Komisyon</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hizmet Bedeli</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Min Komisyon</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center">Yükleniyor...</td>
              </tr>
            ) : rules.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                  Henüz komisyon kuralı yok. Varsayılan %{defaultSettings.default_rate} uygulanıyor.
                </td>
              </tr>
            ) : (
              rules.map((rule) => (
                <tr key={rule.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium">{rule.name}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs ${
                      rule.rule_type === "global" ? "bg-blue-100 text-blue-800" :
                      rule.rule_type === "category" ? "bg-purple-100 text-purple-800" :
                      "bg-green-100 text-green-800"
                    }`}>
                      {rule.rule_type === "global" ? "Genel" :
                       rule.rule_type === "category" ? "Kategori" : "Satıcı"}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-bold text-orange-600">%{rule.commission_rate}</td>
                  <td className="px-6 py-4">{rule.service_fee} ₺</td>
                  <td className="px-6 py-4">{rule.min_commission} ₺</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      rule.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                    }`}>
                      {rule.is_active ? "Aktif" : "Pasif"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleEdit(rule)}
                      className="text-blue-600 hover:text-blue-800 mr-3"
                    >
                      Düzenle
                    </button>
                    <button
                      onClick={() => handleDelete(rule.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Sil
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Commission Info */}
      <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="font-medium text-yellow-800 mb-2">Komisyon Hesaplama Mantığı</h3>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• <strong>Satış Tutarı</strong> - Platform Komisyonu (%) - Hizmet Bedeli (₺) = <strong>Satıcı Hakedişi</strong></li>
          <li>• Kategori kuralı varsa, o kategorideki ürünlere özel oran uygulanır</li>
          <li>• Satıcı kuralı varsa, o satıcıya özel oran uygulanır</li>
          <li>• Minimum komisyon tutarı belirlenebilir (düşük fiyatlı ürünler için)</li>
        </ul>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-lg w-full mx-4 p-6">
            <h2 className="text-xl font-bold mb-4">
              {editingRule ? "Kural Düzenle" : "Yeni Komisyon Kuralı"}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kural Adı</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kural Türü</label>
                <select
                  value={formData.rule_type}
                  onChange={(e) => setFormData({...formData, rule_type: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="global">Genel (Tüm Ürünler)</option>
                  <option value="category">Kategori Bazlı</option>
                  <option value="vendor">Satıcı Bazlı</option>
                </select>
              </div>

              {formData.rule_type === "category" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="">Seçin...</option>
                    {categories.map(cat => (
                      <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Komisyon (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.commission_rate}
                    onChange={(e) => setFormData({...formData, commission_rate: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hizmet Bedeli (₺)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.service_fee}
                    onChange={(e) => setFormData({...formData, service_fee: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Komisyon (₺)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.min_commission}
                    onChange={(e) => setFormData({...formData, min_commission: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="mr-2"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">Aktif</label>
              </div>

              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingRule(null); }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  {editingRule ? "Güncelle" : "Oluştur"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
